import { useCallback, useEffect, useMemo, useRef } from "react";
import type { Row } from "../types.ts";
import { LedgerRow } from "./LedgerRow.tsx";
import { useStickyHead } from "../useStickyHead.ts";

function sortByDueDate(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => {
    if (a.dueDate !== b.dueDate) return a.dueDate.localeCompare(b.dueDate);
    return rows.indexOf(a) - rows.indexOf(b);
  });
}

type Props = {
  rows: Row[];
  startBalance: number;
  focusedDateRowId: string | null;
  setFocusedDateRowId: (id: string | null) => void;
  onPatch: (id: string, patch: Partial<Row>) => void;
  onDelete: (id: string) => void;
  onSnooze: (id: string) => void;
  onAddAfter: (dueDate: string, afterId: string) => void;
};

export function LedgerTable({
  rows,
  startBalance,
  focusedDateRowId,
  setFocusedDateRowId,
  onPatch,
  onDelete,
  onSnooze,
  onAddAfter,
}: Props) {
  const frozenOrderRef = useRef<string[] | null>(null);

  useEffect(() => {
    if (focusedDateRowId === null) {
      frozenOrderRef.current = null;
    } else if (frozenOrderRef.current === null) {
      frozenOrderRef.current = sortByDueDate(rows).map((r) => r.id);
    }
  }, [focusedDateRowId, rows]);

  const sortedRows = useMemo(() => {
    const frozen = focusedDateRowId !== null ? frozenOrderRef.current : null;
    if (frozen === null) return sortByDueDate(rows);
    const byId = new Map(rows.map((r) => [r.id, r]));
    const frozenSet = new Set(frozen);
    const out: Row[] = [];
    for (const id of frozen) {
      const r = byId.get(id);
      if (r) out.push(r);
    }
    for (const r of rows) {
      if (!frozenSet.has(r.id)) out.push(r);
    }
    return out;
  }, [rows, focusedDateRowId]);

  const items = useMemo(() => {
    const result: Array<
      | { kind: "header"; week: number; key: string }
      | { kind: "row"; row: Row; balance: number; key: string }
    > = [];
    let bal = Number(startBalance) || 0;
    let lastWeek: number | null = null;
    for (const row of sortedRows) {
      bal += Number(row.amount) || 0;
      if (row.week !== lastWeek) {
        result.push({ kind: "header", week: row.week, key: `h-${row.id}` });
        lastWeek = row.week;
      }
      result.push({ kind: "row", row, balance: bal, key: row.id });
    }
    return result;
  }, [sortedRows, startBalance]);

  const handleDateFocus = useCallback(
    (id: string) => setFocusedDateRowId(id),
    [setFocusedDateRowId],
  );
  const handleDateBlur = useCallback(
    () => setFocusedDateRowId(null),
    [setFocusedDateRowId],
  );

  const { sentinelRef, stuck } = useStickyHead();

  return (
    <>
      <div ref={sentinelRef} className="thead-sentinel" />
      <table className={stuck ? "thead-stuck" : ""}>
        <thead>
          <tr>
            <th className="col-week">Vecka</th>
            <th className="col-due">Förfallo</th>
            <th className="col-supplier">Leverantör</th>
            <th className="col-amount num">Belopp</th>
            <th className="col-balance num">Saldo</th>
            <th className="col-delete"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) =>
            it.kind === "header" ? (
              <tr key={it.key} className="week-header">
                <td colSpan={6}>Vecka {it.week}</td>
              </tr>
            ) : (
              <LedgerRow
                key={it.key}
                row={it.row}
                balance={it.balance}
                onPatch={onPatch}
                onDelete={onDelete}
                onSnooze={onSnooze}
                onAddAfter={onAddAfter}
                onDateFocus={handleDateFocus}
                onDateBlur={handleDateBlur}
              />
            ),
          )}
        </tbody>
      </table>
    </>
  );
}
